/**
 * R158e · Feb 2026 — Smart Task Hover Card.
 * R158m · Feb 2026 — Redesigned: colour-stripe by priority, breadcrumb
 * pill row (no overflow), inline status + priority editors, quick due
 * date + progress edit. Lazy-fetches the full task doc on first open.
 */
import { useCallback, useState } from "react";
import {
  Calendar, User as UserIcon, FolderKanban, Tag, Clock, MessageSquare,
  ListChecks, AlertTriangle, CheckCircle2, Circle, ExternalLink, Search,
} from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import api from "@/lib/api";
import PriorityIcon from "@/components/tasks/PriorityIcon";
import { toast } from "sonner";

const STATUS_STYLE = {
  open: { label: "Open", cls: "bg-neutral-100 text-neutral-700 border-neutral-300", dot: "bg-neutral-400", Icon: Circle },
  in_progress: { label: "In progress", cls: "bg-amber-100 text-amber-700 border-amber-300", dot: "bg-amber-400", Icon: Clock },
  blocked: { label: "Blocked", cls: "bg-rose-100 text-rose-700 border-rose-300", dot: "bg-rose-500", Icon: AlertTriangle },
  done: { label: "Done", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", dot: "bg-emerald-500", Icon: CheckCircle2 },
};

const PRIORITY_STYLE = {
  urgent: { cls: "text-rose-700 bg-rose-50 border-rose-200", stripe: "bg-rose-500" },
  high: { cls: "text-amber-700 bg-amber-50 border-amber-200", stripe: "bg-amber-500" },
  medium: { cls: "text-neutral-600 bg-neutral-50 border-neutral-200", stripe: "bg-neutral-400" },
  low: { cls: "text-neutral-500 bg-neutral-50 border-neutral-200", stripe: "bg-neutral-300" },
};

function stripHtml(s) {
  if (!s) return "";
  const tmp = String(s).replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&");
  return tmp.replace(/\s+/g, " ").trim();
}

export default function TaskHoverCard({ task, users = [], children, side = "right", align = "start", openDelay = 250, parentTitle = null }) {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tried, setTried] = useState(false);
  const [local, setLocal] = useState({}); // Local overrides from inline edits
  const [statusOpen, setStatusOpen] = useState(false);
  const [prioOpen, setPrioOpen] = useState(false);
  const [asgOpen, setAsgOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [asgQuery, setAsgQuery] = useState("");
  const [latestComment, setLatestComment] = useState(null);
  // R158u · Feb 2026 — Controlled open + lock so the hover card stays
  // open while the native date picker is active (previously it dismissed
  // as soon as the user's cursor left the trigger bounds).
  const [hoverOpen, setHoverOpen] = useState(false);
  const [locked, setLocked] = useState(false);

  const load = useCallback(async () => {
    if (tried || loading || !task?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${task.id}`);
      setFull(data);
      // Lazy-load workspace members for the inline assignee picker.
      if (data?.workspace_id) {
        try {
          const { data: m } = await api.get(`/workspaces/${data.workspace_id}/members`);
          setMembers(m || []);
        } catch { /* silent */ }
      }
      // R160cc · Feb 2026 — Pull the latest human comment from the
      // task thread so the hover card doubles as a mini-context
      // preview. System audit rows (`kind === "system"`) are skipped;
      // client-visible rows are included so PMs see everything a
      // stakeholder can see.
      try {
        const { data: comments } = await api.get(`/tasks/${task.id}/comments`, { params: { include_client: true } });
        if (Array.isArray(comments) && comments.length) {
          const human = comments.filter((c) => (c?.kind || "user") !== "system");
          if (human.length) setLatestComment(human[human.length - 1]);
        }
      } catch { /* silent */ }
    } catch { /* keep lean fallback */ }
    finally { setLoading(false); setTried(true); }
  }, [task?.id, tried, loading]);

  const t = { ...task, ...(full || {}), ...local };
  const status = STATUS_STYLE[t.status] || STATUS_STYLE.open;
  const StatusIcon = status.Icon;
  const prio = PRIORITY_STYLE[t.priority || "medium"] || PRIORITY_STYLE.medium;
  const assignee = t.assignee_id ? users.find((u) => u.id === t.assignee_id) : null;
  const assigneeName = t.assignee_name || assignee?.name || (t.assignee_id ? "" : "Unassigned");
  const overdue = t.due_date && new Date(t.due_date) < new Date() && t.status !== "done";
  const descSnippet = stripHtml(t.description || t.description_html || "").slice(0, 220);
  const tags = Array.isArray(t.tags) ? t.tags.slice(0, 6) : [];
  const subtaskDone = Array.isArray(t.subtasks) ? t.subtasks.filter((s) => s.done).length : null;
  const subtaskTotal = Array.isArray(t.subtasks) ? t.subtasks.length : null;
  const progress = typeof t.progress_percent === "number" ? t.progress_percent : null;

  const patch = async (fields) => {
    if (!task?.id) return true;
    // R158u · Feb 2026 — Reschedule Approval Workflow parity with Tasks.jsx.
    // When changing an existing due_date / start_date, prompt for a
    // justification. If the caller lacks the approve permission the
    // backend returns { reschedule_pending: { ... } } — we roll back the
    // optimistic write and surface a toast so the user knows their edit
    // is awaiting approval.
    const DATE_FIELDS = ["start_date", "due_date"];
    const changedDateKeys = DATE_FIELDS.filter((k) => {
      if (!(k in fields)) return false;
      const newV = fields[k] || null;
      const oldV = t?.[k] || null;
      if (newV === oldV) return false;
      if (!oldV) return false; // first-time set — no reason needed
      return true;
    });
    if (changedDateKeys.length > 0 && !fields.date_change_reason) {
      const reason = window.prompt(
        `Why is the ${changedDateKeys.join(" / ")} changing? (logged to activity)`,
        "",
      );
      if (reason === null) return false; // cancelled
      if (!(reason || "").trim()) {
        toast.error("A justification comment is required to change dates");
        return false;
      }
      fields = { ...fields, date_change_reason: reason.trim() };
    }
    // Optimistic
    setLocal((prev) => ({ ...prev, ...fields }));
    try {
      const { data: server } = await api.patch(`/tasks/${task.id}`, fields);
      if (server && server.reschedule_pending) {
        // Roll back plan-date optimistic writes; server did NOT persist.
        const req = server.reschedule_pending;
        toast.success("Reschedule request submitted — pending approval");
        setLocal((prev) => {
          const rolled = { ...prev };
          for (const k of Object.keys(req.changes || {})) {
            rolled[k] = req.changes[k].from || null;
          }
          return rolled;
        });
        return true;
      }
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Update failed";
      toast.error(msg);
      // Roll back
      setLocal((prev) => {
        const rolled = { ...prev };
        for (const k of Object.keys(fields)) delete rolled[k];
        return rolled;
      });
      return false;
    }
  };

  return (
    <HoverCard
      open={hoverOpen || locked}
      onOpenChange={(open) => {
        if (locked && !open) return; // don't close while editing
        setHoverOpen(open);
        if (open) load();
      }}
      openDelay={openDelay}
      closeDelay={150}
    >
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side={side}
        align={align}
        avoidCollisions
        collisionPadding={16}
        className="w-[420px] max-w-[calc(100vw-32px)] p-0 rounded-none shadow-xl overflow-hidden"
        data-testid={`task-hover-${task?.id}`}
      >
        {/* Priority stripe */}
        <div className="flex">
          <div className={`w-1 shrink-0 ${prio.stripe}`} />
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="p-3 border-b border-border">
              <div className="flex items-center gap-1.5 flex-wrap mb-2">
                <Popover open={statusOpen} onOpenChange={setStatusOpen}>
                  <PopoverTrigger asChild>
                    <button
                      className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${status.cls} hover:ring-1 hover:ring-primary/30`}
                      data-testid={`task-hover-status-${task?.id}`}
                    >
                      <StatusIcon className="w-3 h-3" /> {status.label}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="bottom" align="start" className="w-36 p-1 rounded-none">
                    {Object.entries(STATUS_STYLE).map(([k, s]) => (
                      <button
                        key={k}
                        onClick={() => { setStatusOpen(false); patch({ status: k }); }}
                        className={`w-full text-left text-xs px-2 py-1 hover:bg-muted flex items-center gap-2 ${t.status === k ? "bg-primary/10" : ""}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${s.dot}`} /> {s.label}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
                <Popover open={prioOpen} onOpenChange={setPrioOpen}>
                  <PopoverTrigger asChild>
                    <button
                      className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${prio.cls} hover:ring-1 hover:ring-primary/30`}
                      title="Click to change priority"
                      data-testid={`task-hover-prio-${task?.id}`}
                    >
                      <PriorityIcon priority={t.priority || "medium"} className="w-3 h-3" />
                      {t.priority || "medium"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="bottom" align="start" className="w-36 p-1 rounded-none">
                    {["urgent", "high", "medium", "low"].map((p) => (
                      <button
                        key={p}
                        onClick={() => { setPrioOpen(false); patch({ priority: p }); }}
                        className={`w-full text-left text-xs px-2 py-1 hover:bg-muted flex items-center gap-2 capitalize ${t.priority === p ? "bg-primary/10" : ""}`}
                      >
                        <PriorityIcon priority={p} className="w-3 h-3" /> {p}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
                <div className="ml-auto text-[10px] font-mono text-muted-foreground shrink-0">
                  {t.id ? `#${String(t.id).slice(0, 6)}` : ""}
                </div>
              </div>
              <div className="text-sm font-medium leading-snug break-words">
                {t.title || <span className="italic text-muted-foreground">Untitled task</span>}
              </div>
              {/* R160gg · Feb 2026 — Parent task caption on subtask
                  hover cards. Uses the value passed from the caller
                  (planning board TaskRow) so we don't need an extra
                  API call to resolve the parent's title. */}
              {parentTitle && (
                <div className="mt-1 flex items-center gap-1 text-[10px] uppercase tracking-widest text-primary" data-testid={`task-hover-parent-${t.id}`}>
                  <span aria-hidden>↳</span>
                  <span>Subtask of</span>
                  <span className="font-semibold normal-case tracking-normal text-foreground truncate max-w-[220px]">{parentTitle}</span>
                </div>
              )}
              {/* Breadcrumb (own row so it never overflows sideways) */}
              {(t.client_name || t.brand_name || t.project_name) && (
                <div className="mt-2 flex items-start gap-1 text-[11px] text-muted-foreground">
                  <FolderKanban className="w-3 h-3 shrink-0 mt-0.5" />
                  <span className="flex-1 min-w-0 break-words leading-snug">
                    {[t.client_name, t.brand_name, t.project_name].filter(Boolean).join(" › ")}
                  </span>
                </div>
              )}
            </div>

            {/* Body */}
            <div className="p-3 space-y-2 text-[12px]">
              {descSnippet ? (
                <div className="text-muted-foreground whitespace-pre-wrap leading-snug line-clamp-4">
                  {descSnippet}{descSnippet.length >= 220 ? "…" : ""}
                </div>
              ) : (
                loading ? <div className="text-muted-foreground italic">Loading…</div> : null
              )}

              {/* R160cc · Feb 2026 — Latest comment from the task
                  thread. Renders below the description so hovering
                  gives instant context of the current conversation.
                  System audit rows are already filtered out on load. */}
              {latestComment && (() => {
                const raw = stripHtml(latestComment.body_html || latestComment.body || latestComment.body_text || "");
                if (!raw) return null;
                const snippet = raw.slice(0, 180);
                const author = latestComment.author_name || latestComment.user_name || latestComment.name || "Someone";
                const when = latestComment.created_at ? new Date(latestComment.created_at) : null;
                const whenLabel = when && !isNaN(when) ? when.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
                const isClient = (latestComment.visibility || "") === "client";
                return (
                  <div className="border-l-2 border-primary/40 pl-2 pr-1 py-1 bg-muted/40" data-testid={`task-hover-latest-comment-${task?.id}`}>
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                      <MessageSquare className="w-3 h-3" />
                      <span className="font-semibold text-foreground normal-case tracking-normal truncate">{author}</span>
                      {isClient && <span className="text-[9px] font-semibold text-primary">CLIENT</span>}
                      {whenLabel && <span className="ml-auto shrink-0 text-[9px]">{whenLabel}</span>}
                    </div>
                    <div className="text-muted-foreground whitespace-pre-wrap leading-snug line-clamp-3 mt-0.5">
                      {snippet}{raw.length > 180 ? "…" : ""}
                    </div>
                  </div>
                );
              })()}

              {/* Inline editable due date */}
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-1">
                <Popover open={asgOpen} onOpenChange={setAsgOpen}>
                  <PopoverTrigger asChild>
                    <button
                      className="flex items-center gap-1.5 min-w-0 hover:bg-muted/60 px-1 -mx-1 rounded"
                      title="Click to reassign"
                      data-testid={`task-hover-assignee-${task?.id}`}
                    >
                      <UserIcon className={`w-3 h-3 shrink-0 ${assigneeName === "Unassigned" ? "text-muted-foreground/60" : "text-muted-foreground"}`} />
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">Assignee</span>
                      <span className={`ml-auto truncate ${assigneeName === "Unassigned" ? "text-muted-foreground italic" : ""}`}>{assigneeName || "Unassigned"}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="bottom" align="start" className="w-64 p-0 rounded-none" data-testid={`task-hover-assignee-popover-${task?.id}`}>
                    {/* R160bb · Feb 2026 — Searchable, alphabetically
                        sorted assignee picker. Search matches on
                        name OR email so PMs can find teammates by
                        either. Escape or clearing the box closes. */}
                    <div className="p-1.5 border-b border-border">
                      <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-muted-foreground pointer-events-none" />
                        <input
                          autoFocus
                          value={asgQuery}
                          onChange={(e) => setAsgQuery(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Escape") { setAsgQuery(""); setAsgOpen(false); } }}
                          placeholder="Search a member…"
                          className="w-full h-7 pl-7 pr-2 text-xs border border-input focus:border-primary outline-none bg-background"
                          data-testid={`task-hover-assignee-search-${task?.id}`}
                        />
                      </div>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                      {(() => {
                        const q = asgQuery.trim().toLowerCase();
                        const list = [...members]
                          .map((m) => {
                            const uid = m.user_id || m.id;
                            const name = m.user?.name || m.name || m.user?.email || m.email || (uid || "").slice(0, 8);
                            const email = m.user?.email || m.email || "";
                            return { uid, name, email };
                          })
                          .filter(({ uid }) => !!uid)
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .filter(({ name, email }) => !q || name.toLowerCase().includes(q) || email.toLowerCase().includes(q));
                        return (
                          <>
                            {!q && (
                              <button
                                onClick={() => { setAsgOpen(false); setAsgQuery(""); patch({ assignee_id: null }); }}
                                className={`w-full text-left text-xs px-2 py-1 hover:bg-muted italic text-muted-foreground ${!t.assignee_id ? "bg-primary/10" : ""}`}
                                data-testid={`task-hover-assignee-opt-unassigned-${task?.id}`}
                              >Unassigned</button>
                            )}
                            {list.map(({ uid, name }) => (
                              <button
                                key={uid}
                                onClick={() => { setAsgOpen(false); setAsgQuery(""); patch({ assignee_id: uid, assignee_name: name }); }}
                                className={`w-full text-left text-xs px-2 py-1 hover:bg-muted flex items-center gap-2 ${t.assignee_id === uid ? "bg-primary/10" : ""}`}
                                data-testid={`task-hover-assignee-opt-${uid}-${task?.id}`}
                              >
                                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[9px] font-semibold flex items-center justify-center shrink-0">
                                  {name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                </span>
                                <span className="truncate">{name}</span>
                              </button>
                            ))}
                            {members.length === 0 && (
                              <div className="px-2 py-2 text-[11px] text-muted-foreground italic">Loading members…</div>
                            )}
                            {members.length > 0 && list.length === 0 && q && (
                              <div className="px-2 py-2 text-[11px] text-muted-foreground italic">No teammate matches “{asgQuery}”.</div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </PopoverContent>
                </Popover>
                <div className="flex items-center gap-1.5 min-w-0">
                  <Calendar className={`w-3 h-3 shrink-0 ${overdue ? "text-rose-600" : "text-muted-foreground"}`} />
                  <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">Due</span>
                  <input
                    type="date"
                    value={(t.due_date || "").slice(0, 10)}
                    onMouseDown={() => setLocked(true)}
                    onFocus={() => setLocked(true)}
                    onKeyDown={(e) => { if (e.key === "Escape") { setLocked(false); e.currentTarget.blur(); } }}
                    onBlur={() => {
                      // Give the native picker a beat to commit; then release the lock.
                      setTimeout(() => setLocked(false), 200);
                    }}
                    onChange={async (e) => {
                      const val = e.target.value || null;
                      // Keep locked through the async round-trip so a prompt / toast
                      // doesn't dismiss the card mid-flow.
                      setLocked(true);
                      await patch({ due_date: val });
                      setLocked(false);
                    }}
                    className={`ml-auto bg-transparent text-[11px] tabular-nums border-0 outline-none w-24 text-right ${overdue ? "text-rose-600 font-medium" : ""}`}
                    data-testid={`task-hover-due-${task?.id}`}
                  />
                </div>
                {t.start_date && <MetaRow icon={Calendar} label="Start" value={t.start_date.slice(0, 10)} />}
                {typeof t.est_hours === "number" && t.est_hours > 0 && (
                  <MetaRow icon={Clock} label="Estimate" value={`${t.est_hours}h`} />
                )}
              </div>

              {/* Progress + subtasks */}
              {(progress !== null || (subtaskTotal || 0) > 0) && (
                <div className="pt-1 space-y-1">
                  {progress !== null && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <input
                        type="range" min="0" max="100" step="5"
                        value={progress}
                        onChange={(e) => setLocal((p) => ({ ...p, progress_percent: parseInt(e.target.value, 10) }))}
                        onMouseUp={(e) => patch({ progress_percent: parseInt(e.target.value, 10) })}
                        onTouchEnd={(e) => patch({ progress_percent: parseInt(e.target.value, 10) })}
                        className="flex-1 accent-emerald-500"
                        data-testid={`task-hover-progress-${task?.id}`}
                      />
                      <span className="tabular-nums text-muted-foreground w-9 text-right">{progress}%</span>
                    </div>
                  )}
                  {(subtaskTotal || 0) > 0 && (
                    <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                      <ListChecks className="w-3 h-3" /> Subtasks · {subtaskDone}/{subtaskTotal}
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {tags.length > 0 && (
                <div className="flex items-start gap-1 flex-wrap pt-1">
                  <Tag className="w-3 h-3 text-muted-foreground mt-1" />
                  {tags.map((tag) => (
                    <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-neutral-100 text-neutral-700 border border-neutral-200">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* R159s · Feb 2026 — Effort vs Actual + Billing status.
                   R159bb · Feb 2026 — "Actual" now aggregates every
                   effort accumulator (manual + timesheet + actual)
                   so the value on hover matches the Total-Effort chip
                   on the card and the Finance Report's Total column. */}
              {(() => {
                const totalLogged =
                  (Number(t.manual_effort_hours) || 0)
                  + (Number(t.logged_hours) || Number(t.timesheet_hours) || 0)
                  + (Number(t.actual_hours) || 0);
                const totalDisplay = totalLogged > 0 ? totalLogged.toFixed(1) : (t.actual_hours ?? t.logged_hours ?? t.timesheet_hours);
                const showEffortRow = (t.estimated_hours != null || totalLogged > 0 || t.billable || t.billed || t.paid);
                if (!showEffortRow) return null;
                const est = Number(t.estimated_hours || t.est_hours) || 0;
                const over = est > 0 && totalLogged > est;
                return (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1 text-[11px]">
                    {(t.estimated_hours != null || totalLogged > 0) && (
                      <span className="inline-flex items-center gap-1 text-muted-foreground" title={`Estimated ${t.estimated_hours ?? "—"} h · Total logged (manual + timesheet + actual) ${totalDisplay ?? "—"} h`}>
                        <Clock className="w-3 h-3" />
                        <span className="tabular-nums">{t.estimated_hours ?? "—"}h</span>
                        <span className="opacity-60">→</span>
                        <span className={`tabular-nums font-medium ${over ? "text-rose-600" : "text-emerald-700"}`}>
                          {totalDisplay ?? "—"}h
                        </span>
                      </span>
                    )}
                    {t.billable && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-700 text-[10px] uppercase tracking-widest">
                        Billable
                      </span>
                    )}
                    {t.billed && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-sky-300 bg-sky-50 text-sky-700 text-[10px] uppercase tracking-widest">
                        Billed
                      </span>
                    )}
                    {t.paid && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-purple-300 bg-purple-50 text-purple-700 text-[10px] uppercase tracking-widest">
                        Paid
                      </span>
                    )}
                  </div>
                );
              })()}
              {false && (
                <div />
              )}

              {t.comment_count ? (
                <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 pt-1">
                  <MessageSquare className="w-3 h-3" /> {t.comment_count} comment{t.comment_count === 1 ? "" : "s"}
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="px-3 py-1.5 border-t border-border text-[10px] text-muted-foreground bg-muted/30 flex items-center gap-3">
              <span>Chips + due + progress are editable inline</span>
              {t.project_id && (
                <button
                  onClick={() => window.open(`/app/projects/${t.project_id}`, "_blank", "noopener,noreferrer")}
                  className="ml-auto inline-flex items-center gap-1 text-primary hover:underline"
                  data-testid={`task-hover-open-project-${task?.id}`}
                >
                  Project <ExternalLink className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function MetaRow({ icon: Icon, label, value, highlight = "", muted = false }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <Icon className={`w-3 h-3 shrink-0 ${muted ? "text-muted-foreground/60" : "text-muted-foreground"}`} />
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">{label}</span>
      <span className={`ml-auto truncate ${highlight || (muted ? "text-muted-foreground italic" : "")}`}>{value}</span>
    </div>
  );
}
